import { useState, useEffect, useRef } from 'react';
import { Info, AlertTriangle, CheckCircle } from 'lucide-react';
import { carService } from '../services/api';

const MaxPriceTooltip = ({ car }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showTooltip, setShowTooltip] = useState(false);
  const tooltipRef = useRef(null);
  const badgeRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    const fetchMaxPrice = async () => {
      if (!car || !car.query) {
        setLoading(false);
        return;
      }

      // Stagger requests to avoid overwhelming the backend
      await new Promise(r => setTimeout(r, Math.random() * 2000));
      if (cancelled) return;

      try {
        const result = await carService.getMaxPrice({
          query: car.query,
          year: car.vehicle_year,
        });

        if (!cancelled && result.success) {
          setData(result.data);
        }
      } catch {
        // silently fail - tooltip just won't show
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchMaxPrice();
    return () => { cancelled = true; };
  }, [car]);

  if (loading || !data || !data.precio_max_sugerido) return null;

  const carPrice = car.currency_id === 'USD' ? car.price : null;
  const maxPrice = data.precio_max_sugerido;
  const isOverpriced = carPrice && carPrice > maxPrice;
  const isUnderpriced = carPrice && carPrice <= maxPrice;

  const formatPrice = (price) =>
    new Intl.NumberFormat('es-AR', { minimumFractionDigits: 0 }).format(price);

  const formatPercent = (value) => {
    if (value == null) return '-';
    return `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`;
  };

  const formatFactor = (value) => {
    if (value == null) return '-';
    return `×${value.toFixed(3)}`;
  };

  const confidenceLabel = {
    alta: { text: 'Alta', color: 'text-[#48a877]' },
    media: { text: 'Media', color: 'text-yellow-400' },
    baja: { text: 'Baja', color: 'text-[#c75d5d]' },
  };

  const fallbackLabel = {
    exact: 'Exacto (query+versión+año+km)',
    no_km: 'Sin categoría km',
    version: 'Solo query+versión',
    query_only: 'Solo query (genérico)',
  };

  const confidence = confidenceLabel[data.confidence_level] || { text: data.confidence_level, color: 'text-gray-400' };
  const fallback = fallbackLabel[data.fallback_level] || data.fallback_level;

  // Badge colors
  let badgeClasses, badgeIcon, badgeText;
  if (!carPrice) {
    // Can't compare (ARS price), show neutral
    badgeClasses = 'bg-[#232321] border-[#3a3835] text-[#8a8577]';
    badgeIcon = <Info className="w-3 h-3" />;
    badgeText = `Max: US$ ${formatPrice(maxPrice)}`;
  } else if (isOverpriced) {
    badgeClasses = 'bg-[#c75d5d]/15 border-[#c75d5d]/30 text-[#e88a8a]';
    badgeIcon = <AlertTriangle className="w-3 h-3" />;
    badgeText = `Sobre max: US$ ${formatPrice(maxPrice)}`;
  } else {
    badgeClasses = 'bg-[#48a877]/15 border-[#48a877]/30 text-[#48a877]';
    badgeIcon = <CheckCircle className="w-3 h-3" />;
    badgeText = `Max: US$ ${formatPrice(maxPrice)}`;
  }

  return (
    <div className="relative inline-flex" ref={badgeRef}>
      <button
        type="button"
        className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] font-mono transition-colors cursor-help ${badgeClasses}`}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        onClick={(e) => {
          e.stopPropagation();
          setShowTooltip(!showTooltip);
        }}
        aria-label="Precio máximo sugerido"
      >
        {badgeIcon}
        <span>{badgeText}</span>
      </button>

      {showTooltip && (
        <div
          ref={tooltipRef}
          className="absolute z-50 bottom-full left-0 mb-2 w-64 p-3 rounded-lg bg-[#1c1c1a] border border-[#3a3835] shadow-xl shadow-black/40 text-xs"
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
        >
          {/* Arrow */}
          <div className="absolute top-full left-4 w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[6px] border-t-[#3a3835]" />

          <div className="space-y-2">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-[#2a2926] pb-2">
              <span className="font-semibold text-[#e8e4de]">Precio Máx. Sugerido</span>
              <span className="font-bold font-mono text-[#e8e4de]">US$ {formatPrice(maxPrice)}</span>
            </div>

            {/* Desglose */}
            <div className="space-y-1.5">
              <div className="flex justify-between">
                <span className="text-[#8a8577]">P75 del segmento</span>
                <span className="font-mono text-[#e8e4de]">US$ {formatPrice(data.precio_p75)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#8a8577]">Absorción mercado</span>
                <span className="font-mono text-[#e8e4de]">{formatFactor(data.factor_absorption)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#8a8577]">Tiempo de venta</span>
                <span className="font-mono text-[#e8e4de]">{formatFactor(data.factor_sell_time)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#8a8577]">Tendencia precios</span>
                <span className="font-mono text-[#e8e4de]">
                  {formatFactor(data.factor_trend)}
                  {data.price_trend_30d != null && (
                    <span className={`ml-1 ${data.price_trend_30d < 0 ? 'text-[#c75d5d]' : 'text-[#48a877]'}`}>
                      ({formatPercent(data.price_trend_30d)})
                    </span>
                  )}
                </span>
              </div>
            </div>

            {/* Meta */}
            <div className="border-t border-[#2a2926] pt-2 space-y-1">
              <div className="flex justify-between">
                <span className="text-[#8a8577]">Confianza</span>
                <span className={`font-medium ${confidence.color}`}>{confidence.text}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#8a8577]">Muestra</span>
                <span className="font-mono text-[#e8e4de]">{data.sample_size} vehículos</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#8a8577]">Nivel fallback</span>
                <span className="text-[#8a8577] text-[10px]">{fallback}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MaxPriceTooltip;
